/**
 * @file analyticsIngestFilters.ts
 * @description `before_send` hooks that decide what we refuse to pay to ingest.
 *
 * Two separate concerns, both about volume rather than content (URL scrubbing
 * lives in telemetryUrlSanitizer):
 *
 *   - `isBenignBrowserNoise` drops known-junk exceptions outright.
 *   - `createEventRateLimiter` caps any one event name, as a backstop against
 *     a runaway caller.
 */

import type { CaptureResult } from 'posthog-js'

/**
 * "ResizeObserver loop completed with undelivered notifications" / "loop limit
 * exceeded" is the browser reporting that an observer callback re-laid-out the
 * page and the remaining work rolled to the next frame. It's benign by spec,
 * unactionable, and fires per-frame from any resizing UI — it accounted for 99%
 * of our captured exceptions and nothing was ever wrong.
 *
 * Matches on `$exception_list`, which is what the browser SDK actually sends.
 * The flat `$exception_values` / `$exception_types` arrays that show up in
 * PostHog queries are derived server-side and do not exist on the event here,
 * so matching those would silently never fire.
 */
export function isBenignBrowserNoise(result: CaptureResult | null): CaptureResult | null {
  if (!result || result.event !== '$exception') return result

  const exceptions = result.properties?.$exception_list
  if (!Array.isArray(exceptions) || exceptions.length === 0) return result

  // `every`, not `some`: an exception chain can hold several entries (an error
  // plus its causes). If any entry is a real error, the capture must survive —
  // only a chain that is noise through and through gets dropped.
  const isNoise = exceptions.every(entry => {
    const value = (entry as { value?: unknown } | null)?.value
    return typeof value === 'string' && value.startsWith('ResizeObserver loop')
  })
  return isNoise ? null : result
}

/**
 * A runaway loop in product code — telemetry wired to a retry that never
 * succeeds, say — looks exactly like legitimate traffic at the ingest layer,
 * and it bills at full rate until someone reads the invoice. This is the
 * backstop for that class of bug: no single event name can exceed the budget,
 * whatever the caller does.
 *
 * A single per-session total, deliberately NOT a per-minute window. Legitimate
 * traffic here is bursty but bounded — a folder drop fires one `upload_started`
 * per file, so any rate cap loose enough for a big batch is useless against the
 * incident this exists for (a reconnect loop at a steady ~46/min). Bounded
 * user-driven fan-out never approaches the total; only a loop does. The cap is
 * deliberately far above any real per-session count (busiest legit event:
 * ~130/person/day), so tripping it means a bug rather than a power user. It is
 * a circuit breaker, not a sampler — do not lower it to shape traffic.
 */
export const MAX_EVENTS_PER_NAME_PER_SESSION = 1000

/**
 * Emitted once per offending event name so a tripped breaker is visible in
 * PostHog itself rather than only as a suspicious absence.
 */
export const RATE_LIMITED_EVENT = 'analytics_rate_limited'

/**
 * Events posthog-js emits for its own bookkeeping. Dropping these corrupts
 * session replay and person identity instead of saving money, and none of them
 * are driven by product code that could loop.
 */
const NEVER_LIMITED = new Set(['$snapshot', '$identify', '$groupidentify', '$create_alias', '$set', '$set_once'])

export interface EventRateLimiterOptions {
  maxPerSession?: number
  /** Called once per event name, the first time that name trips the cap. */
  onLimit?: (event: string, limit: number) => void
}

/**
 * Build a `before_send` hook that drops any event name exceeding its
 * per-session budget. Once tripped, that name stays dropped for the rest of
 * the page lifetime — a loop that produced 1,000 captures is not going to
 * produce useful data with more.
 */
export function createEventRateLimiter(
  options: EventRateLimiterOptions = {},
): (result: CaptureResult | null) => CaptureResult | null {
  const maxPerSession = options.maxPerSession ?? MAX_EVENTS_PER_NAME_PER_SESSION
  const onLimit = options.onLimit

  const sessionTotals = new Map<string, number>()
  const reported = new Set<string>()

  return function rateLimitCapture(result: CaptureResult | null): CaptureResult | null {
    if (!result) return result

    const event = result.event
    // The breaker's own alarm is exempt, else a tripped breaker could silence
    // the very event that reports it.
    if (typeof event !== 'string' || event === RATE_LIMITED_EVENT || NEVER_LIMITED.has(event)) {
      return result
    }

    const total = (sessionTotals.get(event) ?? 0) + 1
    sessionTotals.set(event, total)
    if (total <= maxPerSession) return result

    if (!reported.has(event)) {
      reported.add(event)
      onLimit?.(event, maxPerSession)
    }
    return null
  }
}
